// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { formatAmount } from '../../lib/eventLog.js';
import { formatDate } from '../../lib/formatLocale.js';

interface SearchResult {
  txId: string;
  date: string;
  description: string;
  num: string;
  notes?: string;
  amount: number;
  accountId: string;
  accountName: string;
  currency: string;
}

const inputCls = 'bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none w-full';
const labelCls = 'block text-xs text-text-muted mb-1';

export function GlobalSearch() {
  const { t } = useTranslation(['search', 'common', 'reports']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  const results = useMemo<SearchResult[]>(() => {
    if (!query.trim() && !dateFrom && !dateTo && !amountMin && !amountMax) return [];

    const q = query.toLowerCase().trim();
    const minCents = amountMin ? Math.round(parseFloat(amountMin) * 100) : null;
    const maxCents = amountMax ? Math.round(parseFloat(amountMax) * 100) : null;

    const out: SearchResult[] = [];

    for (const tx of ledger.transactions.values()) {
      if (tx.voided) continue;

      if (dateFrom && tx.date < dateFrom) continue;
      if (dateTo && tx.date > dateTo) continue;

      const matchesText =
        !q ||
        tx.description.toLowerCase().includes(q) ||
        (tx.notes ?? '').toLowerCase().includes(q) ||
        tx.num.toLowerCase().includes(q) ||
        tx.splits.some((s) => (s.memo ?? '').toLowerCase().includes(q));

      if (!matchesText) continue;

      for (const split of tx.splits) {
        const absAmount = Math.abs(split.amount);
        if (minCents !== null && absAmount < minCents) continue;
        if (maxCents !== null && absAmount > maxCents) continue;

        const account = ledger.accounts.get(split.accountId);
        out.push({
          txId: tx.id,
          date: tx.date,
          description: tx.description,
          num: tx.num,
          notes: tx.notes,
          amount: split.amount,
          accountId: split.accountId,
          accountName: account?.name ?? t('common:unknown'),
          currency: tx.currency,
        });
      }
    }

    out.sort((a, b) => b.date.localeCompare(a.date));
    return out.slice(0, 200);
  }, [query, dateFrom, dateTo, amountMin, amountMax, ledger.transactions, ledger.accounts]);

  return (
    <div className="flex-1 overflow-auto p-4">
      <h2 className="text-md font-semibold text-text mb-4">{t('title')}</h2>

      <div className="bg-bg-surface border border-border p-4 mb-4">
        <div className="flex gap-3 flex-wrap">
          <div className="flex-1 min-w-48">
            <label className={labelCls}>{t('filters.descMemoNum')}</label>
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('filters.searchPlaceholder')}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t('filters.dateFrom')}</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
            />
          </div>
          <div>
            <label className={labelCls}>{t('filters.dateTo')}</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
            />
          </div>
          <div>
            <label className={labelCls}>{t('filters.amountMin')}</label>
            <input
              type="number"
              step="0.01"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
              placeholder="0.00"
              className="w-28 bg-bg border border-border text-text text-sm px-3 py-2 font-mono focus:border-accent focus:outline-none"
            />
          </div>
          <div>
            <label className={labelCls}>{t('filters.amountMax')}</label>
            <input
              type="number"
              step="0.01"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
              placeholder="0.00"
              className="w-28 bg-bg border border-border text-text text-sm px-3 py-2 font-mono focus:border-accent focus:outline-none"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => { setQuery(''); setDateFrom(''); setDateTo(''); setAmountMin(''); setAmountMax(''); }}
              className="px-3 py-2 text-sm text-text-muted border border-border hover:bg-bg-elevated transition-colors"
            >
              {t('clear')}
            </button>
          </div>
        </div>
      </div>

      {results.length === 0 ? (
        <div className="text-center py-16 text-text-subtle">
          {query || dateFrom || dateTo || amountMin || amountMax
            ? t('reports:empty.noTransactionsFound')
            : t('emptyPrompt')}
        </div>
      ) : (
        <div className="bg-bg-surface border border-border overflow-hidden">
          <div className="px-4 py-1.5 border-b border-border bg-bg-elevated text-xs text-text-muted">
            {results.length === 200
              ? t('resultCountCapped', { count: results.length })
              : t('resultCount', { count: results.length })}
          </div>
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.date')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2">{t('columns.num')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2">{t('columns.description')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2">{t('columns.account')}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr
                  key={`${r.txId}-${r.accountId}-${i}`}
                  className="border-b border-border last:border-0 hover:bg-bg-elevated cursor-pointer transition-colors"
                  onClick={() => navigate(`/accounts/${r.accountId}`)}
                >
                  <td className="py-2 px-4 whitespace-nowrap text-text-muted font-mono text-xs">{formatDate(r.date, { month: 'short', day: '2-digit', year: 'numeric' })}</td>
                  <td className="py-2 px-2 font-mono text-xs text-text-subtle">{r.num}</td>
                  <td className="py-2 px-2 text-text max-w-64 truncate">
                    {r.description}
                    {r.notes && <span className="text-text-subtle ml-2 text-xs">{r.notes}</span>}
                  </td>
                  <td className="py-2 px-2 text-accent hover:underline text-xs">
                    {r.accountName}
                  </td>
                  <td className={`py-2 px-4 text-right font-mono font-medium text-sm ${r.amount >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {r.amount >= 0 ? '+' : ''}{formatAmount(r.amount, r.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
